import { formatScore, type Entrant, type GameResult, type Round, type Tournament } from '@ctd/core';
import { useRef, useState, type KeyboardEvent, type RefObject } from 'react';
import { PrintHeader } from '../components/PrintHeader';
import { PageHeading, PrintButton, TableRegion } from '../components/ui';
import { useDispatch, useSnapshot } from '../context';
import {
  RESULT_OPTIONS,
  byeLabel,
  moveForKey,
  resultForKey,
  resultFromValue,
  resultValue,
  sameResult,
} from '../lib/results';
import { pairingBlocker, pendingGames, scoresBefore, tryPairNext } from '../lib/rounds';

export function RoundsTab() {
  const { tournament: t } = useSnapshot();
  const dispatch = useDispatch();
  // null follows the latest round, so a newly paired round is shown at once.
  const [chosen, setChosen] = useState<number | null>(null);
  const [pairError, setPairError] = useState<string | null>(null);
  const tableRef = useRef<HTMLTableSectionElement>(null);
  const shown = chosen !== null && chosen <= t.rounds.length ? chosen : t.rounds.length;
  const round = t.rounds[shown - 1];
  const next = t.rounds.length + 1;
  const blocker = pairingBlocker(t);

  function pair(): void {
    const attempt = tryPairNext(t);
    if (!attempt.ok) {
      setPairError(attempt.error);
      return;
    }
    setPairError(null);
    if (dispatch(attempt.event, `Round ${next} paired.`)) {
      setChosen(null);
      // Put the cursor on board 1, ready for results.
      requestAnimationFrame(() => {
        tableRef.current?.querySelector<HTMLSelectElement>('select')?.focus();
      });
    }
  }

  function unpair(r: Round): void {
    const entered = r.games.length - pendingGames(r);
    const warning =
      entered > 0 ? ` ${entered} result${entered > 1 ? 's' : ''} will be removed with it.` : '';
    if (!window.confirm(`Unpair round ${r.number}?${warning}`)) return;
    setPairError(null);
    dispatch({ type: 'RoundUnpaired', round: r.number }, `Round ${r.number} unpaired.`);
  }

  return (
    <section>
      <div className="page-head no-print">
        <PageHeading>Rounds</PageHeading>
        <div className="actions">
          {next <= t.settings.rounds && (
            <button
              type="button"
              className="button primary"
              onClick={pair}
              disabled={blocker !== null}
              aria-describedby={blocker !== null ? 'pair-blocker' : undefined}
            >
              Pair round {next}
            </button>
          )}
        </div>
      </div>
      {blocker !== null && (
        <p id="pair-blocker" className="hint no-print">
          {blocker}
        </p>
      )}
      {pairError !== null && (
        <div className="error-box no-print" role="alert">
          <h3>Round {next} could not be paired</h3>
          <p>{pairError}</p>
          <p className="hint">
            Check the players (withdrawals, byes) or choose another pairing system in Setup, then
            try again.
          </p>
        </div>
      )}

      {t.rounds.length > 0 && (
        <nav aria-label="Rounds" className="round-picker no-print">
          <ul>
            {t.rounds.map((r) => (
              <li key={r.number}>
                <button
                  type="button"
                  className="button small"
                  aria-pressed={r.number === shown}
                  onClick={() => setChosen(r.number)}
                >
                  Round {r.number}
                </button>
              </li>
            ))}
          </ul>
        </nav>
      )}

      {round === undefined ? (
        <p className="empty">
          No round is paired yet. Add the players, number them by rating, then pair round 1.
        </p>
      ) : (
        <RoundView
          t={t}
          round={round}
          isLast={round.number === t.rounds.length}
          tableRef={tableRef}
          onUnpair={() => unpair(round)}
          onResult={(board, result) =>
            dispatch({ type: 'ResultEntered', round: round.number, board, result })
          }
          onPublish={() =>
            dispatch(
              { type: 'RoundPublished', round: round.number },
              `Round ${round.number} published.`,
            )
          }
        />
      )}
    </section>
  );
}

interface RoundViewProps {
  t: Tournament;
  round: Round;
  isLast: boolean;
  tableRef: RefObject<HTMLTableSectionElement | null>;
  onUnpair: () => void;
  onResult: (board: number, result: GameResult) => void;
  onPublish: () => void;
}

function RoundView({ t, round, isLast, tableRef, onUnpair, onResult, onPublish }: RoundViewProps) {
  const byId = new Map(t.players.map((p) => [p.id, p]));
  const scores = scoresBefore(t, round.number);
  const pending = pendingGames(round);
  const label = (id: string): string => byId.get(id)?.name ?? id;

  function onKeyDown(e: KeyboardEvent<HTMLSelectElement>, index: number): void {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    const selects = [...(tableRef.current?.querySelectorAll('select') ?? [])];
    const result = resultForKey(e.key);
    if (result !== null) {
      e.preventDefault();
      const g = round.games[index];
      if (g !== undefined && !sameResult(g.result, result)) onResult(g.board, result);
      // A result moves on to the next board; clearing stays put.
      if (result.white !== '*') selects[Math.min(index + 1, selects.length - 1)]?.focus();
      return;
    }
    const to = moveForKey(e.key, index, selects.length);
    if (to !== null) {
      e.preventDefault();
      selects[to]?.focus();
      return;
    }
    // Left and right would change a closed select's value in some browsers.
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') e.preventDefault();
  }

  return (
    <div className="round-view">
      <PrintHeader title={`Round ${round.number} pairings`} />
      <div className="round-head">
        <h3 className="round-title">
          Round {round.number}{' '}
          <span className={`badge ${round.published ? 'badge-ok' : 'badge-muted'}`}>
            {round.published ? 'Published' : 'Not published'}
          </span>{' '}
          <span className={`badge ${pending === 0 ? 'badge-ok' : 'badge-warn'}`}>
            {pending === 0
              ? 'All results in'
              : `${round.games.length - pending} of ${round.games.length} results`}
          </span>
        </h3>
        <div className="actions no-print">
          {!round.published && (
            <button type="button" className="button" onClick={onPublish}>
              Publish
            </button>
          )}
          {isLast && (
            <button type="button" className="button danger" onClick={onUnpair}>
              Unpair round {round.number}
            </button>
          )}
          <PrintButton label="Print pairings" />
        </div>
      </div>
      <p className="hint no-print" id="keys-help">
        Fast entry: move to a board&rsquo;s result and press <kbd>1</kbd> white wins, <kbd>0</kbd>{' '}
        black wins, <kbd>5</kbd> or <kbd>=</kbd> draw, <kbd>+</kbd> or <kbd>-</kbd> forfeit win for
        white or black, <kbd>Delete</kbd> clears. <kbd>↑</kbd> <kbd>↓</kbd> move between boards.
      </p>
      <TableRegion label={`Round ${round.number} pairings`}>
        <table className="data-table pairings">
          <caption className="visually-hidden">
            Round {round.number} pairings and results, by board
          </caption>
          <thead>
            <tr>
              <th scope="col" className="num">
                Bd
              </th>
              <th scope="col" className="num opt">
                No.
              </th>
              <th scope="col">White</th>
              <th scope="col" className="num opt">
                Pts
              </th>
              <th scope="col" className="result-col">
                Result
              </th>
              <th scope="col" className="num opt">
                Pts
              </th>
              <th scope="col">Black</th>
              <th scope="col" className="num opt">
                No.
              </th>
            </tr>
          </thead>
          <tbody ref={tableRef}>
            {round.games.map((g, i) => {
              const w = byId.get(g.white);
              const b = byId.get(g.black);
              const value = resultValue(g.result);
              return (
                <tr key={g.board}>
                  <td className="num">{g.board}</td>
                  <td className="num opt">{w?.pairingNumber}</td>
                  <td>
                    <PlayerName p={w} fallback={g.white} />
                  </td>
                  <td className="num opt">{formatScore(scores.get(g.white) ?? 0)}</td>
                  <td className="result-col">
                    <select
                      className="result-select"
                      value={value}
                      aria-label={`Result, board ${g.board}: ${label(g.white)} against ${label(g.black)}`}
                      aria-describedby="keys-help"
                      data-board={g.board}
                      onChange={(e) => {
                        const r = resultFromValue(e.target.value);
                        if (r !== null) onResult(g.board, r);
                      }}
                      onKeyDown={(e) => onKeyDown(e, i)}
                    >
                      {value === '' && (
                        <option value="">{`${g.result.white}:${g.result.black}`}</option>
                      )}
                      {RESULT_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                    <span className="print-only">{printResult(value)}</span>
                  </td>
                  <td className="num opt">{formatScore(scores.get(g.black) ?? 0)}</td>
                  <td>
                    <PlayerName p={b} fallback={g.black} />
                  </td>
                  <td className="num opt">{b?.pairingNumber}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </TableRegion>
      {round.byes.length > 0 && (
        <>
          <h4>Not playing this round</h4>
          <ul className="bye-list">
            {round.byes.map((b) => (
              <li key={b.player}>
                {label(b.player)}: {byeLabel(b.kind).toLowerCase()}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function printResult(value: string): string {
  const o = RESULT_OPTIONS.find((x) => x.value === value);
  if (o === undefined || o.value === '*') return '';
  return o.label.split(' (')[0] ?? o.label;
}

function PlayerName({ p, fallback }: { p: Entrant | undefined; fallback: string }) {
  if (p === undefined) return <>{fallback}</>;
  return (
    <>
      {p.title ? <abbr className="title">{p.title}</abbr> : null} {p.name}
      {p.rating > 0 && <span className="rating"> {p.rating}</span>}
    </>
  );
}
